/**
 * TextField — un campo de texto con su etiqueta, su error y su ayuda.
 *
 * Lo que aporta sobre un `<input>` suelto es la **costura de accesibilidad**,
 * que en el repo se escribe a mano y se olvida: `htmlFor` contra un id
 * generado, `aria-invalid` cuando hay error, y `aria-describedby` apuntando al
 * error y a la ayuda. ⚠️ Eso último es lo que hace que un lector de pantalla
 * lea el mensaje de error al llegar al campo, en vez de dejarlo como un texto
 * rojo suelto que solo se ve.
 *
 * ⚠️ El error y la ayuda **no se muestran juntos**: con los dos, el error tapa
 * a la ayuda. Un campo que dice a la vez «formato inválido» y «usar el formato
 * dd-mm-aaaa» obliga a leer dos veces para saber cuál manda.
 */
import { useId } from 'react'
import type { ComponentPropsWithRef, CSSProperties, HTMLAttributes, InputHTMLAttributes, ReactNode } from 'react'

export interface TextFieldProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'className' | 'style' | 'onChange'> {
  label?: ReactNode
  /** ⚠️ También `number`, que es lo que acepta un `<input>`: restringirlo a
   *  `string` obligaba a convertir en cada pantalla con un campo numérico. */
  value?: string | number
  onChange?: InputHTMLAttributes<HTMLInputElement>['onChange']
  placeholder?: string
  error?: ReactNode
  hint?: ReactNode
  disabled?: boolean
  readOnly?: boolean
  required?: boolean
  /**
   * Convierte el control en un `<textarea>`, conservando toda la costura de
   * accesibilidad.
   *
   * ⚠️ Está acá y no en un componente aparte porque un formulario suele mezclar
   * los dos —las notas internas viven junto al nombre y al teléfono— y dos
   * componentes distintos dejarían la etiqueta de uno con otra tipografía que
   * la del vecino. Medido en el repo: 20 `<textarea>` repartidos en formularios
   * que ya usan campos de texto.
   */
  multiline?: boolean
  /** Alto del `<textarea>` en líneas. Sin `multiline` no hace nada. */
  rows?: number
  /**
   * Control más bajo (32px en vez de 38) para formularios largos.
   *
   * ⚠️ Medido sobre un formulario de 9 campos: el control aporta 63px de los
   * 199 que se pueden recortar; el resto está en el AIRE entre campos, que lo
   * decide la pantalla con su `space-y-*`. Bajar solo el control y dejar
   * `space-y-5` recorta un 9%; hacer las dos cosas, un 28%.
   *
   * 🟢 En pantalla táctil el bloque `pointer: coarse` lo devuelve al mínimo de
   * 44px, así que comprimir acá no rompe el área de toque en un teléfono.
   */
  dense?: boolean
  id?: string
  className?: string
  style?: CSSProperties
  /**
   * Lo que va al `<input>` y no al contenedor: `type`, `maxLength`,
   * `autoComplete`…
   *
   * 🔴 **Y es por donde entra `react-hook-form`**: `inputProps={register('rut')}`.
   * Eso obliga a que el tipo admita `ref`, `onChange`, `onBlur` y `name`, que
   * es lo que `register()` devuelve. Sin el `ref` el campo se vería igual y el
   * formulario no lo leería — el peor modo de fallar, porque no hay error.
   * Medido en el repo: 142 de los 285 campos con etiqueta usan `register()`.
   *
   * ⚠️ El spread va DESPUÉS de `value`/`onChange`, así que cuando se pasa un
   * `register()` manda él. Es lo correcto: un campo no puede estar controlado
   * por los dos a la vez.
   */
  inputProps?: Omit<ComponentPropsWithRef<'input'>, 'id'>
}

export function TextField({
  label,
  value,
  onChange,
  placeholder,
  error,
  hint,
  disabled = false,
  readOnly = false,
  required = false,
  multiline = false,
  rows,
  dense = false,
  id: idProp,
  className = '',
  inputProps,
  ...rest
}: TextFieldProps) {
  const auto = useId()
  const id = idProp || `cl-${auto}`
  const idError = `${id}-err`
  const idAyuda = `${id}-hint`

  /**
   * Los atributos van en UN objeto y no repetidos en las dos ramas: son la
   * costura de accesibilidad entera, y escribirlos dos veces es exactamente
   * cómo `<textarea>` terminaría sin el `aria-describedby` que `<input>` sí
   * tiene.
   */
  const comunes = {
    id,
    className: dense ? 'cl-input cl-input--sm' : 'cl-input',
    value,
    placeholder,
    disabled,
    readOnly,
    // 🔴 `aria-required` y NO el `required` nativo. Los dos anuncian el campo
    // como obligatorio, pero el nativo además **secuestra el envío**: el
    // navegador frena el submit y muestra su propio «Complete este campo»,
    // genérico y en el idioma del navegador, antes de que react-hook-form
    // llegue a mostrar el mensaje que la pantalla escribió («La Razón Social
    // es obligatoria»). Medido en el repo: solo 5 de los 44 formularios ponen
    // `noValidate`, así que en los otros 39 el nativo ganaría.
    'aria-required': required || undefined,
    'aria-invalid': error ? true : undefined,
    'aria-describedby':
      [error ? idError : null, hint ? idAyuda : null].filter(Boolean).join(' ') || undefined,
  }

  return (
    <div className={`cl-field ${dense ? 'cl-field--dense' : ''} ${className}`.trim()} {...rest}>
      {label && (
        <label className="cl-label" htmlFor={id}>
          {label}
          {required && (
            // `aria-hidden` porque el asterisco ya lo anuncia el `required` del
            // input: leerlo dos veces es ruido.
            <span aria-hidden="true" style={{ color: 'rgb(var(--color-danger))' }}>
              {' *'}
            </span>
          )}
        </label>
      )}
      {multiline ? (
        // ⚠️ El cast va acá, UNA vez y localizado: `onChange` e `inputProps`
        // están tipados contra `<input>`, y lo único que las pantallas usan de
        // ese evento es `target.value`, que los dos elementos tienen. Tipar el
        // componente entero contra la unión obligaría a castear en cada
        // consumidor, que es lo contrario de lo que se busca.
        <textarea
          {...comunes}
          rows={rows ?? 3}
          onChange={onChange as React.ChangeEventHandler<HTMLTextAreaElement> | undefined}
          {...(inputProps as React.ComponentPropsWithRef<'textarea'>)}
        />
      ) : (
        <input {...comunes} onChange={onChange} {...inputProps} />
      )}
      {error && (
        <div className="cl-error" id={idError} role="alert">
          {error}
        </div>
      )}
      {hint && !error && (
        <div className="cl-hint" id={idAyuda}>
          {hint}
        </div>
      )}
    </div>
  )
}
